import { create } from 'zustand';
import type { SessionUserProfileDetailedResponse } from '@pillar-portal/api-types';

interface AuthState {
    user: SessionUserProfileDetailedResponse | null;
    audit_id: number | null;
    enableJavascriptConsole: boolean;
    isAuthenticated: boolean;
    isLoading: boolean;
    activeRoleAssignmentId: number | null;

    // NEW STATE: Flag to stop the profile query loop during logout/redirect
    isLoggingOut: boolean;

    setUser: (user: SessionUserProfileDetailedResponse | null) => void;
    setLoading: (loading: boolean) => void;
    setEnableJavascriptConsole: (enabled: boolean) => void;
    setActiveRoleAssignmentId: (activeRoleAssignmentId: number | null) => void;
    logout: () => void;

    // NEW ACTION: Setter for the new state flag
    setIsLoggingOut: (status: boolean) => void;
}

const DEFAULT_CONSOLE_LOGGING_ENABLED: boolean = import.meta.env.DEV;

export const useAuthStore = create<AuthState>((set) => ({
    user: null,
    audit_id: null,
    enableJavascriptConsole: DEFAULT_CONSOLE_LOGGING_ENABLED,
    isAuthenticated: false,
    isLoading: true,
    activeRoleAssignmentId: null,

    // Initialize new state
    isLoggingOut: false,

    setUser: (user) =>
        set({
            user,
            isAuthenticated: !!user,
            isLoading: false,
        }),

    setLoading: (loading) => set({ isLoading: loading }),

    setEnableJavascriptConsole: (enabled) => set({ enableJavascriptConsole: enabled }),

    setActiveRoleAssignmentId: (activeRoleAssignmentId) => set({ activeRoleAssignmentId }),

    logout: () =>
        set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            enableJavascriptConsole: DEFAULT_CONSOLE_LOGGING_ENABLED,
            activeRoleAssignmentId: null,
            // NOTE: We do not set isLoggingOut here. The ProtectedRoute
            // component handles setting this flag *before* calling logout()
            // and redirecting to prevent race conditions.
        }),

    // Implement new action
    setIsLoggingOut: (status) => set({ isLoggingOut: status }),
}));
